import { format, getTime, isSameYear, UTCDate } from '@/utils/dates';
import { TransactionStatus, TransactionType } from '@seapoint/server/enums';
import { ColumnDef } from '@tanstack/react-table';
import { ChevronRight, Sparkles } from 'lucide-react';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Badge } from '../../../../design-system/badge/badge';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { getInstitutionDetails } from '../../../../pages/accounts/utils/get-institution-details';
import { getVendorLogo } from '../../../../pages/accounts/utils/get-vendor-details';
import { TransactionView } from '../../../../types/entity-types';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';

const ICON_SIZE = 16;
const ICON_COLOR = '#86868B';

export const transactionsTableColumnsDefinition: ColumnDef<TransactionView>[] = [
  {
    id: 'fspDate',
    // Gets the date in milliseconds so we can filter by date range
    accessorFn: (row) => getTime(row.fspDate),
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Date" />,
    cell: ({ row }) => {
      const transactionDate = row.original.fspDate;
      const currentYear = new UTCDate();
      const dateFormat = isSameYear(transactionDate, currentYear) ? 'dd MMM' : 'dd MMM yyyy';

      return <p className="text-sm text-black">{format(transactionDate, dateFormat)}</p>;
    },
    // Excludes column from global filter
    // https://tanstack.com/table/latest/docs/guide/global-filtering#disable-global-filtering
    enableGlobalFilter: false,
    // Number range inclusion
    filterFn: 'inNumberRange',
    size: 10,
    minSize: 10,
  },
  {
    // Do not remove id - it is needed for txn table views where we display a subset of columns
    id: 'vendor',
    accessorFn: (row) => row.vendor?.name,
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Vendor" />,
    cell: ({ row }) => {
      // AI vendor enrichment for a transaction is completed the transaction is linked to a vendor
      const vendorEnrichmentCompleted = !!row.original.vendorId;

      const vendorName = vendorEnrichmentCompleted ? (row.original.vendor?.name ?? '') : row.original.description;
      const vendorLogo = getVendorLogo(row.original.vendor?.name, row.original.vendor?.logo);
      const avatarConfig = vendorEnrichmentCompleted
        ? { showAvatar: true, avatarUrl: vendorLogo, size: 'sm' as const }
        : { showAvatar: false };

      const reference = row.original.type === TransactionType.TRANSFER ? row.original.reference : null;

      const statusBadge = getTransactionStatusBadge(row.original.status);

      return (
        <Inline space={2} align="center" wrap="nowrap">
          <AvatarNameDescription avatar={avatarConfig} name={vendorName} description={reference} className="min-w-0" />
          {statusBadge}
        </Inline>
      );
    },
    filterFn: 'arrIncludesSome',
    size: 30,
  },
  {
    accessorKey: 'category',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Category" />,
    accessorFn: ({ vendor }) => vendor?.category?.level2,
    cell: ({ row }) => {
      // AI category enrichment for a transaction is completed the transaction vendor is linked to a category
      const categoryEnrichmentCompleted = !!row.original.vendor?.categoryId;

      if (!categoryEnrichmentCompleted) {
        return (
          <Inline space={2} align="center">
            <Sparkles size={14} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Enriching transaction</p>
          </Inline>
        );
      }

      return (
        <AvatarNameDescription
          avatar={
            row.original.vendor?.category?.level2
              ? {
                  showAvatar: true,
                  avatarUrl: row.original.vendor?.category?.icon,
                  size: 'xs',
                  faIcon: {
                    isFaIcon: true,
                    faIconName: row.original.vendor?.category?.icon || '',
                  },
                }
              : { showAvatar: false }
          }
          name={row.original.vendor?.category?.level2 ?? ''}
        />
      );
    },
    filterFn: 'arrIncludesSome',
    size: 25,
  },
  {
    id: 'account',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account" />,
    cell: ({ row }) => {
      const account = row.original.account;
      const institution = account.institution;
      const institutionDetails = institution ? getInstitutionDetails(institution) : undefined;

      // Use institution name if available, otherwise fall back to account displayName
      const accountName = institutionDetails?.name || account.displayName;
      const accountNameWithCurrency = `${accountName} (${account.currency})`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: institutionDetails?.logo, size: 'xs' }}
          name={accountNameWithCurrency}
        />
      );
    },

    enableGlobalFilter: false,
    size: 25,
  },
  {
    id: 'amount',
    accessorKey: 'amount',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Amount" />,
    accessorFn: (row) => {
      // Return signed numeric value: positive for credits (income), negative for debits (expenses)
      // This ensures proper numeric sorting where debits come before credits when ascending
      return getNormalizedAmount({ amount: row.amount, isCredit: row.isCredit });
    },
    cell: ({ row }) => {
      const normalizedAmount = getNormalizedAmount({ amount: row.original.amount, isCredit: row.original.isCredit });
      const normalizedBaseCurrencyAmount = getNormalizedAmount({
        amount: row.original.baseCurrencyAmount,
        isCredit: row.original.isCredit,
      });
      const shouldDisplayBaseCurrencyAmount = row.original.currency !== row.original.baseCurrency;

      return (
        // Arbitrary width to avoid UI jumps when paginating
        // TODO: Tanstack table has a size property, but it doesn't work as expected. Need to investigate further
        // https://github.com/TanStack/table/issues/5115
        <Stack align="end" space={2}>
          <AmountDisplay normalizedAmount={normalizedAmount} currency={row.original.currency} />

          {shouldDisplayBaseCurrencyAmount && (
            <AmountDisplay
              normalizedAmount={normalizedBaseCurrencyAmount}
              currency={row.original.baseCurrency}
              isSecondary
            />
          )}
        </Stack>
      );
    },
    size: 20,
  },
  {
    id: 'chevron',
    accessorKey: 'chevron',
    // No header for the chevron column
    header: () => null,
    cell: () => <ChevronRight size={ICON_SIZE} color={ICON_COLOR} />,
    enableGlobalFilter: false,
    size: 5,
    minSize: 5,
  },
];

const getTransactionStatusBadge = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.PENDING:
      return <Badge shape="pill">Pending</Badge>;
    case TransactionStatus.FAILED:
      return (
        <Badge variant="negative" shape="pill">
          Failed
        </Badge>
      );
    case TransactionStatus.BOOKED:
      // No badge for booked transactions
      return null;
  }
};
